import React, { useEffect, useMemo, useState } from "react";
import { db, auth, storage } from "./firebase";
import { collection, addDoc, doc, getDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { MAJOR_INDIAN_CITIES, SERVICE_CATEGORIES, SERVICE_NAME_OPTIONS } from "./serviceCatalog";
import { optimizeImageForUpload } from "./imageUploadUtils";
import { assertStorageReady, formatUploadError } from "./uploadErrorUtils";
import { EmptyState, PageHeader, StatCard, ToneBadge } from "./petaarUi";

const actionsRowStyle = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
};

const helperListStyle = {
  margin: 0,
  paddingLeft: 18,
  color: "var(--muted)",
  lineHeight: 1.7,
};

export default function AddService({ onBack }) {
  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [city, setCity] = useState("");
  const [category, setCategory] = useState("");
  const [availabilityNote, setAvailabilityNote] = useState("Mon-Sat");
  const [imageUrls, setImageUrls] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [saving, setSaving] = useState(false);
  const [providerStatus, setProviderStatus] = useState("");
  const [cityAutofilled, setCityAutofilled] = useState(false);
  const [categoryAutofilled, setCategoryAutofilled] = useState(false);

  useEffect(() => {
    const loadProviderDefaults = async () => {
      try {
        if (!auth.currentUser?.uid) return;
        const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
        if (!userDoc.exists()) return;
        const data = userDoc.data() || {};
        const profileCity = String(data.city || "").trim();
        if (profileCity) {
          setCity((prev) => prev || profileCity);
          setCityAutofilled(true);
        }
        const profilePrimaryCategory = String(data.primaryCategory || "").trim();
        if (profilePrimaryCategory) {
          setCategory((prev) => prev || profilePrimaryCategory);
          setCategoryAutofilled(true);
        }
        setProviderStatus(data.providerStatus || "pending");
      } catch (_) {
        // Defaults are optional only.
      }
    };
    loadProviderDefaults();
  }, []);

  const uploadImageFile = async (file) => {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error("Login expired. Please login again.");
    await assertStorageReady(storage?.app?.options?.storageBucket || "");
    const optimized = await optimizeImageForUpload(file, {
      maxWidth: 1600,
      maxHeight: 1600,
      quality: 0.82,
      maxOutputBytes: 900 * 1024,
      skipBelowBytes: 220 * 1024,
      outputType: "image/jpeg",
    });
    const safeName = String(optimized.name || file.name || "image")
      .toLowerCase()
      .replace(/[^a-z0-9._-]/g, "-")
      .slice(0, 80);
    const filePath = `serviceImages/${uid}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${safeName}`;
    const fileRef = ref(storage, filePath);
    await uploadBytes(fileRef, optimized, {
      contentType: optimized.type || file.type || "image/jpeg",
      cacheControl: "public,max-age=3600",
    });
    return getDownloadURL(fileRef);
  };

  const onPickImages = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    const maxBytes = 12 * 1024 * 1024;
    const onlyImages = files
      .filter((file) => file.type.startsWith("image/") && file.size <= maxBytes)
      .slice(0, Math.max(0, 6 - imageUrls.length));
    if (onlyImages.length < files.length) {
      alert("Some files were skipped. Use image files up to 12MB each, max 6 photos.");
    }
    if (!onlyImages.length) {
      event.target.value = "";
      return;
    }
    try {
      setUploadingImages(true);
      const urls = await Promise.all(onlyImages.map(uploadImageFile));
      setImageUrls((prev) => [...prev, ...urls].slice(0, 6));
    } catch (error) {
      alert(`Image upload failed: ${formatUploadError(error, storage?.app?.options?.storageBucket)}`);
    } finally {
      setUploadingImages(false);
      event.target.value = "";
    }
  };

  const categoryServices = SERVICE_NAME_OPTIONS.filter((option) => option.category === category);
  const priceBreakup = useMemo(() => {
    const numericPrice = Number(price);
    if (!Number.isFinite(numericPrice) || numericPrice <= 0) {
      return { amount: 0, commission: 0, payout: 0 };
    }
    return {
      amount: numericPrice,
      commission: numericPrice * 0.2,
      payout: numericPrice * 0.8,
    };
  }, [price]);

  const completeness = useMemo(() => {
    const fields = [title, price, city, category, availabilityNote].filter((value) => String(value || "").trim());
    const imageBonus = imageUrls.length ? 1 : 0;
    return Math.min(100, Math.round(((fields.length + imageBonus) / 6) * 100));
  }, [title, price, city, category, availabilityNote, imageUrls.length]);

  const handleAddService = async () => {
    if (!title || !price || !city || !category) {
      alert("Please fill all fields");
      return;
    }

    const numericPrice = Number(price);
    if (!Number.isFinite(numericPrice) || numericPrice < 1) {
      alert("Price must be a number and at least ₹1");
      return;
    }

    try {
      setSaving(true);
      const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
      const userData = userDoc.exists() ? (userDoc.data() || {}) : {};
      const nextProviderStatus = userData.providerStatus || "pending";
      const providerUnavailableDates = Array.isArray(userData.unavailableDates) ? userData.unavailableDates : [];
      const providerUnavailableSlots = Array.isArray(userData.unavailableSlots) ? userData.unavailableSlots : [];
      setProviderStatus(nextProviderStatus);
      await addDoc(collection(db, "services"), {
        title,
        price: numericPrice,
        city,
        category,
        availabilityNote: availabilityNote.trim(),
        imageUrls,
        providerId: auth.currentUser.uid,
        providerEmail: auth.currentUser.email,
        providerStatusSnapshot: nextProviderStatus,
        unavailableDates: providerUnavailableDates,
        unavailableSlots: providerUnavailableSlots,
        isPublic: nextProviderStatus === "approved",
        createdAt: new Date(),
      });

      alert("Service added successfully 🎉");
      setTitle("");
      setPrice("");
      setCity("");
      setCategory("");
      setAvailabilityNote("Mon-Sat");
      setImageUrls([]);
    } catch (error) {
      alert(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="petaar-page">
      <PageHeader
        eyebrow="Provider Services"
        title="Create A New Listing"
        subtitle="Build a clean, trustworthy service page with accurate pricing, location coverage, and real photos."
        actions={
          onBack ? (
            <button className="petaar-button petaar-button--secondary" onClick={onBack}>
              Back To Dashboard
            </button>
          ) : null
        }
      />

      <div className="petaar-stat-grid" style={{ marginBottom: 24 }}>
        <StatCard label="Listing readiness" value={completeness} suffix="%" tone="accent" meta="Based on service details and media" />
        <StatCard label="Customer pays" value={priceBreakup.amount} prefix="₹" decimals={2} tone="default" meta="Visible booking price" />
        <StatCard label="Platform fee" value={priceBreakup.commission} prefix="₹" decimals={2} tone="warning" meta="20% Petaar commission" />
        <StatCard label="Your payout" value={priceBreakup.payout} prefix="₹" decimals={2} tone="success" meta="Estimated provider earning" />
      </div>

      <div className="petaar-split" style={{ alignItems: "start" }}>
        <section className="petaar-card" style={{ display: "grid", gap: 18 }}>
          <div style={{ display: "grid", gap: 10 }}>
            {providerStatus && providerStatus !== "approved" ? (
              <ToneBadge tone="warning">This service stays hidden until admin approval ({providerStatus})</ToneBadge>
            ) : (
              <ToneBadge tone="success">Approved providers go live immediately after save</ToneBadge>
            )}
          </div>

          <div className="petaar-form-grid">
            <select
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setTitle("");
              }}
            >
              <option value="">Select service category</option>
              {SERVICE_CATEGORIES.map((cat) => (
                <option key={cat.id} value={cat.name}>{cat.name}</option>
              ))}
            </select>

            <select
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={!category}
            >
              <option value="">Select service name</option>
              {categoryServices.map((service) => (
                <option key={service.name} value={service.name}>{service.name}</option>
              ))}
            </select>

            <input
              placeholder="Price in INR"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />

            <select value={city} onChange={(e) => setCity(e.target.value)}>
              <option value="">Select city</option>
              {MAJOR_INDIAN_CITIES.map((cityName) => (
                <option key={cityName} value={cityName}>{cityName}</option>
              ))}
            </select>
          </div>

          {(cityAutofilled || categoryAutofilled) ? (
            <div className="petaar-notice info" style={{ display: "grid", gap: 4 }}>
              {cityAutofilled ? <span>City was prefilled from your provider profile.</span> : null}
              {categoryAutofilled ? <span>Category was prefilled from your provider profile.</span> : null}
            </div>
          ) : null}

          <input
            placeholder="Availability note, for example Mon-Sat, 9am-7pm"
            value={availabilityNote}
            onChange={(e) => setAvailabilityNote(e.target.value)}
          />

          <div className="petaar-card" style={{ padding: 18, display: "grid", gap: 14, background: "var(--surface-alt)" }}>
            <div className="petaar-section-title" style={{ marginBottom: 0 }}>
              <div>
                <div className="petaar-kicker">Media</div>
                <h3 style={{ fontSize: "1.35rem" }}>Service images</h3>
              </div>
              <label htmlFor="service-images-input" className="petaar-button petaar-button--secondary" style={{ cursor: uploadingImages ? "not-allowed" : "pointer" }}>
                {uploadingImages ? "Uploading..." : "Upload Images"}
              </label>
            </div>
            <input
              id="service-images-input"
              type="file"
              accept="image/*"
              multiple
              onChange={onPickImages}
              style={{ display: "none" }}
            />

            {imageUrls.length > 0 ? (
              <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))" }}>
                {imageUrls.map((url, index) => (
                  <div key={`${index}-${url.slice(0, 20)}`} style={{ position: "relative" }}>
                    <img
                      src={url}
                      alt={`service ${index + 1}`}
                      style={{ width: "100%", aspectRatio: "1.2 / 1", objectFit: "cover", borderRadius: 14, border: "1px solid var(--border)" }}
                    />
                    <button
                      onClick={() => setImageUrls((prev) => prev.filter((_, i) => i !== index))}
                      style={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        width: 28,
                        height: 28,
                        borderRadius: 999,
                        border: "none",
                        background: "rgba(17, 24, 39, 0.78)",
                        color: "#fff",
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                icon="📷"
                title="No photos uploaded yet"
                description="Add real service photos so pet owners trust the listing faster."
              />
            )}

            <small style={{ color: "var(--muted)" }}>
              Add up to 6 images. Supported: JPG, PNG, WebP. Each raw file can be up to 12MB before optimization.
            </small>
          </div>

          <div style={actionsRowStyle}>
            <button className="petaar-button" onClick={handleAddService} disabled={saving || uploadingImages}>
              {saving ? "Saving Service..." : "Save Service"}
            </button>
            <button
              className="petaar-button petaar-button--ghost"
              onClick={() => {
                setTitle("");
                setPrice("");
                setCity("");
                setCategory("");
                setAvailabilityNote("Mon-Sat");
                setImageUrls([]);
              }}
              disabled={saving || uploadingImages}
            >
              Reset Form
            </button>
          </div>
        </section>

        <div style={{ display: "grid", gap: 24 }}>
          <section className="petaar-card" style={{ display: "grid", gap: 14 }}>
            <div className="petaar-kicker">Quality</div>
            <h3 style={{ fontSize: "1.5rem" }}>Listing checklist</h3>
            <ul style={helperListStyle}>
              <li>Choose the exact category before selecting the service title.</li>
              <li>Use realistic pricing so customers do not drop during payment.</li>
              <li>Upload real photos, not posters, AI images, or ad creatives.</li>
              <li>Keep availability short and operationally accurate.</li>
              <li>Save city-specific listings if your coverage differs by location.</li>
            </ul>
          </section>

          <section className="petaar-card" style={{ display: "grid", gap: 14 }}>
            <div className="petaar-kicker">What Owners See</div>
            <h3 style={{ fontSize: "1.5rem" }}>Booking summary preview</h3>
            <div className="petaar-list-row" style={{ padding: 18 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: "1.05rem" }}>{title || "Service name"}</div>
                  <div style={{ marginTop: 6, color: "var(--muted)" }}>
                    {category || "Category"} · {city || "City"}
                  </div>
                  <div style={{ marginTop: 6, color: "var(--muted)" }}>
                    {availabilityNote || "Availability note"}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "0.85rem", color: "var(--muted)" }}>Starts from</div>
                  <div style={{ fontSize: "1.6rem", fontWeight: 900 }}>₹{price || "0"}</div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
